/**
 * Logique de filtrage des rows affichées dans la grille Timesheet,
 * extraite de Timesheet.tsx pour être testée unitairement.
 *
 * Contexte : sur une semaine à cheval, on ne veut pas noyer l'employé
 * sous des projets qui ne concernent pas le mois actif. Mais il y a des
 * exceptions :
 *   - Projets vides (fraîchement ajoutés, aucune heure nulle part) : OK
 *   - Projets explicitement ré-ajoutés dans le mois actif
 *   - (BE uniquement) Projets avec ventilation incomplète sur un autre mois
 *     → on les garde visibles pour permettre à l'employé de corriger
 *
 * Hors semaine à cheval : on affiche tout.
 */
import type { GridRow, Ventilation } from '../pages/timesheet/types';
import { dateKey } from './dateUtils';

export interface VisibleRowsInput {
  rows: GridRow[];
  weekDays: Date[];
  isStraddleWeek: boolean;
  isOutsideEntryMonth: (day: Date) => boolean;
  addedProjectIds: Set<string>;
  /** BE uniquement : ventilations courantes, pour détecter les rows à garder
   *  même hors mois actif (ventilation incomplète à corriger). */
  isBE: boolean;
  ventilations: Record<string, Ventilation>;
}

/** Retourne true si le row a au moins une ventilation incomplète. */
export function hasIncompleteVentilation(
  row: GridRow,
  ventilations: Record<string, Ventilation>,
): boolean {
  return Object.entries(row.hours).some(([dKey, h]) => {
    if (!(h > 0)) return false;
    const key = `${row.projectId}_${dKey}`;
    const vent = ventilations[key];
    const totalVent = vent ? vent.etude + vent.suivi + vent.mise_en_service + vent.chantier : 0;
    return Math.abs(totalVent - h) > 0.01;
  });
}

export function computeVisibleRows(input: VisibleRowsInput): GridRow[] {
  const { rows, weekDays, isStraddleWeek, isOutsideEntryMonth, addedProjectIds, isBE, ventilations } = input;

  if (!isStraddleWeek) return rows;

  const activeDayKeys = weekDays
    .filter(day => !isOutsideEntryMonth(day))
    .map(day => dateKey(day));

  return rows.filter(row => {
    const hasActiveHours = activeDayKeys.some(dKey => (row.hours[dKey] || 0) > 0);
    const hasTotalHours = Object.values(row.hours).some(h => h > 0);
    if (hasActiveHours || !hasTotalHours || addedProjectIds.has(row.projectId)) return true;

    // BE : garder visible si au moins une ventilation incomplète (à corriger)
    if (isBE) return hasIncompleteVentilation(row, ventilations);

    return false;
  });
}
